import React from 'react'
import { Box, Stack, Center, Loader, useMantineTheme } from '@mantine/core'
import { useLocation } from 'react-router-dom'
import { useMediaQuery } from '@mantine/hooks'
import Header from './Header'
import Footer from './Footer'
import Error500 from '@/components/common/Error500'
import { useBackendHealthCheck } from '@/hooks/useHealthChecks'
import Adsense from '@/components/common/Adsense'
import { LAYOUT_CONSTANTS } from '@/constants/styles'
import { useSidebarOffset } from '@/hooks/useSidebarOffset'

export interface IProps {
  children: React.ReactNode
}

const MainLayout = (props: IProps) => {
  const { children } = props
  const location = useLocation()
  const theme = useMantineTheme()

  // Media queries for responsive breakpoints (ascending order)
  const isMobile = useMediaQuery(`(max-width: calc(${theme.breakpoints.md} - 1px))`)
  const isTablet = useMediaQuery(`(min-width: ${theme.breakpoints.md}) and (max-width: calc(${theme.breakpoints.lg} - 1px))`)
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.lg}) and (max-width: calc(${theme.breakpoints.xl} - 1px))`)
  const isDesktopXl = useMediaQuery(`(min-width: ${theme.breakpoints.xl})`)
  const { topOffset, maxHeight } = useSidebarOffset()

  // Determine which backend to check based on URL path
  const getRequiredBackend = (pathname: string): 'parse' | 'strapi' | null => {
    if (pathname.startsWith('/etf') || pathname.startsWith('/screener')) return 'parse'
    if (pathname.startsWith('/blog') || pathname.startsWith('/news')) return 'strapi'
    return null // No health check needed for other paths
  }

  const requiredBackend = getRequiredBackend(location.pathname)
  const { data, isLoading, error } = useBackendHealthCheck(requiredBackend)

  // Determine what to render in main content area
  const renderMainContent = () => {
    // No health check needed
    if (!requiredBackend) {
      return children
    }

    // Loading state
    if (isLoading) {
      return (
        <Center>
          <Loader />
        </Center>
      )
    }

    // Error state
    if (error) {
      return <Error500 />
    }

    return children
  }

  return (
    <Stack style={{ minHeight: '100vh', background: 'var(--background-gradient)' }} gap={0} align="center">
      <Header />

      <Stack maw={isDesktopXl ? 1328 : 1280} w="100%" gap={0}>
        <Box
          style={{
            backgroundColor: '#DEE2E6',
            height: '130px',
            width: isDesktopXl ? `calc(100% - ${(LAYOUT_CONSTANTS.SIDEBAR_WIDTH + 24) * 2}px)` : '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            alignSelf: 'center'
          }}>
          {/* Mobile: 375x130 */}
          {isMobile && <Adsense slot="6414791493" height={130} width={375} isResponsive={false} />}
          {/* Tablet: 744x130 */}
          {isTablet && <Adsense slot="9284497415" height={130} width={744} isResponsive={false} />}
          {/* Desktop: 1200x130 */}
          {isDesktop && <Adsense slot="8036398924" height={130} width={1200} isResponsive={false} />}
          {/* Desktop XL: 1040x130 */}
          {isDesktopXl && <Adsense slot="7437656482" height={130} width={1040} isResponsive={false} />}
        </Box>

        <Box style={{ display: 'flex', flex: 1 }}>
          {/* Left Sidebar Container - Only show on desktop XL */}
          {isDesktopXl && (
            <Box style={{ width: LAYOUT_CONSTANTS.SIDEBAR_WIDTH + 24, flexShrink: 0 }}>
              <Box
                mt={24}
                style={{
                  width: LAYOUT_CONSTANTS.SIDEBAR_WIDTH,
                  position: 'sticky',
                  top: topOffset,
                  maxHeight: maxHeight,
                  overflow: 'hidden',
                  backgroundColor: '#DEE2E6',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                <Adsense slot="2895752881" height={786} width={LAYOUT_CONSTANTS.SIDEBAR_WIDTH} />
                {/* <Box
                  style={{
                    backgroundColor: '#DEE2E6',
                    height: 786,
                    width: LAYOUT_CONSTANTS.SIDEBAR_WIDTH
                  }}
                /> */}
              </Box>
            </Box>
          )}

          {/* Main Content */}
          <Box
            component="main"
            style={{
              flex: 1,
              background: 'var(--background-gradient)',
              minHeight: '100vh',
              overflowX: 'hidden'
            }}
            p="lg">
            {renderMainContent()}
          </Box>

          {/* Right Sidebar Container - Only show on desktop XL */}
          {isDesktopXl && (
            <Box style={{ width: LAYOUT_CONSTANTS.SIDEBAR_WIDTH + 24, flexShrink: 0 }}>
              <Box
                ml={'auto'}
                mt={24}
                style={{
                  position: 'sticky',
                  width: LAYOUT_CONSTANTS.SIDEBAR_WIDTH,
                  alignSelf: 'end',
                  top: topOffset,
                  maxHeight: maxHeight,
                  overflow: 'hidden',
                  backgroundColor: '#DEE2E6',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                <Adsense slot="3513272936" height={786} width={LAYOUT_CONSTANTS.SIDEBAR_WIDTH} />
                {/* <Box
                  style={{
                    backgroundColor: '#DEE2E6',
                    height: 786,
                    width: LAYOUT_CONSTANTS.SIDEBAR_WIDTH
                  }}
                /> */}
              </Box>
            </Box>
          )}
        </Box>
      </Stack>
      <Footer />
    </Stack>
  )
}

export default MainLayout
